import { useState } from "react";
import moment from "moment";
import { IconButton, Tooltip, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAddReferencesToNote } from "../../../../../hooks/panelFilters/useAddReferencesToNote";
import WitnessReorderModal from "./WitnessReorderModal/WitnessReorderModal";
import { useDeleteWitness } from "../../../../../hooks/panelClient/useDeleteWitness";
import { useDeleteNote } from "../../../../../hooks/useDeleteNote";

const SortableNoteItem = ({
  nota,
  onEdit,
  onDelete, // Not used directly, we use the hook
  token,
  refresh,
  idDocument,
  idSeccion,
}) => {
  const [witnessModalOpen, setWitnessModalOpen] = useState(false);
  const [dragOverNote, setDragOverNote] = useState(false);
  const { addReferencesToNote } = useAddReferencesToNote();
  const { deleteWitness } = useDeleteWitness(token);
  const { deleteNote, loading: loadingDeleteNote } = useDeleteNote(token);

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [witnessToDelete, setWitnessToDelete] = useState(null);
  const [isOnlyWitness, setIsOnlyWitness] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: nota.id_sintesis,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
    position: isDragging ? "relative" : "static",
    opacity: isDragging ? 0.85 : 1,
  };

  const stopPropagation = (e) => e.stopPropagation();

  const handleDropOnNote = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverNote(false);

    try {
      const dataStr = e.dataTransfer.getData("application/json");
      if (!dataStr) return;
      const data = JSON.parse(dataStr);

      if (data.type === "NOTES_DRAG" && data.ids && data.ids.length > 0) {
        const result = await addReferencesToNote(
          nota.id_sintesis,
          idDocument,
          idSeccion,
          data.ids,
          moment().format("YYYY-MM-DD"),
          token,
        );

        if (result.success) {
          refresh();
        }
      }
    } catch (err) {
      console.error("Error procesando drop de testigos en la nota:", err);
    }
  };

  const handleRequestDeleteWitness = (testigo, e) => {
    e.preventDefault();
    e.stopPropagation();
    setWitnessToDelete(testigo);
    setIsOnlyWitness(nota.testigo && nota.testigo.length === 1);
    setConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (isOnlyWitness) {
        // Eliminar la nota
        const cvcapsula = nota.cvcapsula || nota.capclave || nota.CAPCLAVE || "";
        const isSuccess = await deleteNote(nota.id_sintesis, cvcapsula);
        if (isSuccess) refresh();
      } else {
        // Eliminar solo el testigo
        const isSuccess = await deleteWitness(witnessToDelete.witness_id);
        if (isSuccess) refresh();
      }
    } catch (error) {
      console.error("Error al eliminar", error);
    } finally {
      setIsDeleting(false);
      setConfirmDialog(false);
      setWitnessToDelete(null);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: dragOverNote
          ? "rgba(76, 175, 80, 0.1)"
          : "transparent",
        outline: dragOverNote ? "2px solid #4caf50" : "none",
        borderRadius: "4px",
        transition: "all 0.2s ease",
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverNote(true);
      }}
      onDragLeave={(e) => {
        e.stopPropagation();
        setDragOverNote(false);
      }}
      onDrop={handleDropOnNote}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "default",
          gap: "8px",
        }}
      >
        <div
          {...attributes}
          {...listeners}
          style={{
            display: "flex",
            alignItems: "center",
            cursor: isDragging ? "grabbing" : "grab",
            color: "#aaa",
            padding: "0 4px",
            borderRadius: "4px",
            flexShrink: 0,
            touchAction: "none",
          }}
          aria-label="Arrastrar nota"
        >
          <DragIndicatorIcon fontSize="small" />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontSize: "13px",
              fontWeight: "bold",
              marginBottom: "4px",
              wordWrap: "break-word",
            }}
          >
            {nota.titulo}
          </p>

          {nota.testigo?.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                display: "inline-flex",
                marginBottom: "6px",
                padding: "1px 8px",
                borderRadius: "10px",
                backgroundColor: "rgba(25, 118, 210, 0.08)",
              }}
            >
              <NewspaperOutlinedIcon sx={{ fontSize: 13, color: "primary.main" }} />
              <Typography
                variant="caption"
                sx={{ color: "primary.main", fontWeight: 500, lineHeight: 1.6 }}
              >
                {nota.testigo.length} {nota.testigo.length === 1 ? "medio" : "medios"}
              </Typography>
            </Stack>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {nota.testigo?.map((testigo, index) => (
              <Chip
                key={index}
                label={testigo.medio}
                clickable
                component="a"
                href={testigo.url}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                onDelete={(e) => handleRequestDeleteWitness(testigo, e)}
                sx={{
                  height: "auto",
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    display: "block",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                  },
                }}
              />
            ))}
          </Stack>
        </div>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          flexShrink={0}
          onPointerDown={stopPropagation}
        >
          <Tooltip title="Reordenar Testigos">
            <span>
              <IconButton
                color="success"
                onClick={() => setWitnessModalOpen(true)}
                disabled={!nota.testigo || nota.testigo.length < 2}
                size="small"
              >
                <LowPriorityIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Editar nota">
            <IconButton color="primary" onClick={onEdit} size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </div>
      <hr />

      <WitnessReorderModal
        open={witnessModalOpen}
        onClose={() => setWitnessModalOpen(false)}
        nota={nota}
        token={token}
        refresh={refresh}
      />

      {/* Modal de confirmación para eliminar testigo/nota */}
      <Dialog open={confirmDialog} onClose={isDeleting ? undefined : () => setConfirmDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            ¿Estás seguro que deseas eliminar el testigo <strong>{witnessToDelete?.medio}</strong>?
          </Typography>
          {isOnlyWitness && (
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontStyle: 'italic', p: 1, borderRadius: 1 }}>
              <span style={{ color: "red" }}>Advertencia:</span> Al eliminar este testigo, se eliminará la nota.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)} disabled={isDeleting || loadingDeleteNote} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} disabled={isDeleting || loadingDeleteNote} color="error" variant="contained">
            {isDeleting || loadingDeleteNote ? <CircularProgress size={24} color="inherit" /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SortableNoteItem;